/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { defaultAIsuggestion } from 'utils/constants';
import { ActiveBox, FormValues, Q1Value } from 'models/documents';
import utils from 'utils';
import { InputsContainer, InputQ1Wrapper, InputLabel } from './styles';
import Dropdown from 'components/Input/dropdown/Dropdown';
import Icon, { IconNames } from 'components/Input/Icon';
import CommonButton from 'components/Buttons/CommonButton';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'state/selectors';
import { RootState } from 'models';
import { actionType } from 'utils/constants';
import actions from 'state/actions';
import { AIsuggestion, FieldGroup } from 'models/response';
import service from 'services';
import SuggestionBox from 'components/SuggestionBox';
import intlHelper from 'utils/helpers/intl.helper';
import userHelper from 'utils/helpers/user.helper';

export const useDecimalUpdater = (name: string) => {
  // this update all camp if the decimal scale is changed
  const dispatch = useDispatch();
  const [fieldValuta] = useField('Valuta');
  const valutaCode = fieldValuta.value?.content[0]?.content ?? 2;

  const documentFlatFields = useSelector(selectors.documents.selectDocumentFlatFields);
  // const valutaCode = fieldValuta.value?.content[0].content;

  const valutaFieldValues = documentFlatFields.find(({ fieldName }) => fieldName === 'Valuta')?.hTableValues;
  const decimal = valutaFieldValues?.find(({ code }) => code === valutaCode)?.decimals ?? 2;

  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    if (name !== 'Valuta') return;
    dispatch(actions.documents.setDocumentDecimal(decimal));

    // update currency formik values when decimal scale is changed
    Object.keys(values).forEach((name) => {
      const currencyInput = documentFlatFields.find(
        ({ fieldInputType, fieldName }) => fieldInputType === 3 && fieldName === name,
      );

      if (currencyInput) {
        const field = values[name] as Q1Value;

        const newValue = intlHelper.formatCurrencyField(field, decimal);
        setFieldValue(name, newValue);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimal, dispatch, documentFlatFields, setFieldValue]);
};

interface Props {
  label: string;
  name: string;
  idField: number;
  readonly?: boolean;
  options: { value: string; label: string }[];
  multi?: boolean;
  index?: number;
  removeAction?: () => void;
  addAction?: () => void;
  hasQ3?: boolean;
  protocol: number | null;
  fieldGroup: FieldGroup | null;
}

const List = ({
  options,
  name,
  idField,
  readonly = false,
  multi,
  index,
  removeAction,
  addAction,
  label,
  hasQ3,
  protocol,
  fieldGroup,
}: Props) => {
  useDecimalUpdater(name);
  const [field, , helpers] = useField<Q1Value>(name);
  const { status } = useFormikContext();
  const t = utils.intl.useTranslator();
  const { val } = useSelector((store: RootState) =>
    selectors.app.getConfig(store, actionType.DEFAULT_SELECT_Q1_PLACEHOLDER),
  );
  const [showSuggestionsBox, setShowSuggestionsBox] = useState(false);
  const [suggestions, setSuggestion] = useState<AIsuggestion[] | null>(null);
  const dispatch = useDispatch();
  const icon = hasQ3 ? 'border-all' : 'exclamation-circle';
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const companyName = records.length ? userHelper.getCompany(records, 'companyName') : undefined;

  const disabledCheck = companyName
    ? !utils.user.isActionByCompanyActive(actionType.CAN_EDIT_Q3, companyName ?? undefined) || (readonly && !hasQ3)
    : readonly && !hasQ3;

  const showQ3 = () => {
    if (fieldGroup) {
      dispatch(actions.documents.setBodyColumns(fieldGroup.fieldColumn));
      dispatch(actions.documents.setBalanceField(fieldGroup.balanceField));
      dispatch(actions.documents.setHeaderNameQ3(fieldGroup.name));
      dispatch(actions.documents.setBodyName(name));
      dispatch(actions.documents.setShowBody(true));
      dispatch(actions.documents.setPredictionsData(fieldGroup.predictionsValues));
    }
  };

  const getSuggestions = async () => {
    try {
      if (protocol) {
        const { data } = await service.getAIsuggestions(protocol, idField);
        return data;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getDefaultSuggestion = () => {
    let defaultSuggestion = '';
    if (field.value && typeof field.value.content === 'object') {
      const {
        confidence,
        boundingBox: { x1, x2, y1, y2 },
        pageNumber,
      } = field.value.content[multi && (index || index === 0) ? index : 0];
      // suggestion as an id in this way
      defaultSuggestion = `${confidence}-${pageNumber}-${x1},${y1}-${x2},${y2}`;
    }
    return defaultSuggestion;
  };

  const defaultSuggestion = React.useMemo(getDefaultSuggestion, [field.value]);

  const onIconClick = async () => {
    if (disabledCheck) {
      return;
    }
    dispatch(actions.documents.setActiveBox(null));
    // if suggestions already visible close it
    if (showSuggestionsBox) {
      setShowSuggestionsBox(false);
      return;
    }
    dispatch(actions.documents.setAiSuggestions([]));
    const data = await getSuggestions();
    if (data?.length) {
      const filteredData = data.filter(({ content }) => options.find((option) => option.value === content));
      setSuggestion(filteredData);
      dispatch(actions.documents.setAiSuggestions(data));
    } else {
      !hasQ3 && utils.app.notify('warning', t('no suggestions'), 5000, 'no-suggestions');
    }
    if (hasQ3 && fieldGroup) {
      showQ3();
    } else {
      setShowSuggestionsBox(true);
    }
    return true;
  };

  const onSuggestionSelection = (suggestion: AIsuggestion) => {
    if (field.value) {
      const suggestionValues = { ...suggestion };
      const arrayValues = [...field.value.content];
      suggestionValues.status = 3;
      arrayValues[multi && (index || index === 0) ? index : 0] = suggestionValues;
      helpers.setValue({
        ...field.value,
        content: arrayValues,
      });
      setShowSuggestionsBox(false);
      dispatch(actions.documents.setActiveBox(null));
    }
  };

  const setActiveBox = (value: ActiveBox | null) => {
    dispatch(actions.documents.setActiveBox(value));
  };

  const currentValue = options.find(
    ({ value }) => value === (field.value?.content?.[multi && (index || index === 0) ? index : 0]?.content ?? null),
  );

  const renderInput = () => {
    const isConfirmed = field.value.content[index ?? 0].status === 3;
    let iconName: IconNames = icon;

    // Map old icon names with confirmed attribute to new Icon names
    if (icon === 'border-all') {
      iconName = isConfirmed ? 'table-verified' : 'table-not-verified';
    } else if (icon === 'exclamation-circle') {
      iconName = !isConfirmed ? 'tooltip-filled' : 'exclamation-circle';
    } else if (icon === 'paragraph-field') {
      iconName = isConfirmed ? 'paragraph-confirmed' : 'paragraph';
    }

    const InputIcon = <Icon icon={iconName} disabled={disabledCheck} cursor="default" onClick={onIconClick} />;

    const onChange = (option: { value: string; label: string } | null) => {
      if (field.value) {
        const arrayValues = [...field.value.content];
        arrayValues[multi && (index || index === 0) ? index : 0] = {
          ...defaultAIsuggestion,
          content: option?.value ?? '',
          status: option ? 3 : 1,
        };
        helpers.setValue({
          ...field.value,
          content: arrayValues,
        });
      }
    };

    const InputQ1Component = (
      <>
        <InputLabel title={label}>{!multi ? label : ''}</InputLabel>
        <Dropdown
          id="q1-dropdown"
          isPortal
          placeholder={val ? t(val) : undefined}
          feedbackColor={utils.input.getInputStatusColor(idField, status)}
          hasFeedback={Boolean(utils.input.getInputStatusColor(idField, status))}
          feedbackMessage={utils.input.getInputStatusMessage(idField, status)}
          value={currentValue}
          options={options}
          disabled={readonly}
          onChange={onChange}
          isClearable={!multi}
          minWidthLabel={multi ? '125px' : '200px'}
          width={multi ? '160px' : '200px'}
          closeMenuOnScroll={(event) => {
            if (event.target instanceof Document) {
              const activeElement = event.target.activeElement;
              if (activeElement?.id?.includes('react-select')) return false;
              return true;
            }
            if (event.target instanceof HTMLElement) {
              if (
                event.target?.className?.includes('MenuList') ||
                event.target.parentElement?.className?.includes('menu')
              ) {
                return false;
              }
              return true;
            }
            return true;
          }}
        />
      </>
    );

    return (
      <>
        <InputQ1Wrapper multi={multi} margin={multi ? '0 0px 0 -4px' : '0'}>
          {InputQ1Component}
        </InputQ1Wrapper>
        {InputIcon}
      </>
    );
  };

  return field.value ? (
    <>
      <InputsContainer multi={multi}>
        {multi && addAction && (
          <CommonButton
            id="q1-add-input-btn"
            tooltip={t('add-input')}
            scope="secondary"
            action={addAction}
            customIcon="plus"
            disabled={readonly}
          />
        )}
        {multi && removeAction && (
          <CommonButton
            id="q1-remove-input-btn"
            tooltip={t('add-input')}
            scope="secondary"
            action={removeAction}
            customIcon="minus"
            disabled={readonly}
          />
        )}
        {renderInput()}
      </InputsContainer>
      {suggestions?.length && showSuggestionsBox && !hasQ3 ? (
        <SuggestionBox
          defaultValue={defaultSuggestion}
          suggestions={suggestions}
          onSelection={onSuggestionSelection}
          setActiveBox={setActiveBox}
          marginLeft={multi ? '-124px' : '0px'}
        />
      ) : null}
    </>
  ) : null;
};

export default List;
